import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { GoalsGoalsPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import users from 'modules/core/store/collections/users'

import GoalConfigTable from 'modules/goals/components/Table'
import goalsConfig from 'modules/goals/store/collections/goalsconfig'
import CompetenciesDiscoveryModal from 'modules/job_catalog/components/CompetenciesDiscoveryModal'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import { NewGoalModal } from './NewGoalModal'

type ContentProps = {
  canSeeClockedIn: boolean
}

const Content = ({ canSeeClockedIn }: ContentProps) => {
  const [hideDiscoveryModal, setHideDiscoveryModal] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideCompetenciesDiscoveryModalTip
  )
  const [isOpen, toggle] = useToggle(!hideDiscoveryModal)
  const canCreateJob = policyAuthorizations.for('job_catalog.create_job')
  const canManageCompetencies = users.current.policy.can('competencies.competenciesManage')
  const isRoleEditor = jobRolesCollection.some((x) =>
    policyAuthorizations.for('job_catalog.edit_job', x)
  )
  const showModal = isOpen && (canCreateJob || isRoleEditor || canManageCompetencies)

  useAsyncData(
    () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('job_catalog.create_job'),
        policyAuthorizations.authorizePolicies('job_catalog.edit_job'),
      ]),
    []
  )

  return (
    <>
      <GoalConfigTable canSeeClockedIn={canSeeClockedIn} />
      {showModal && (
        <CompetenciesDiscoveryModal
          onClose={() => {
            setHideDiscoveryModal(true)
            toggle(false)()
          }}
          handleOnComplete={() => setHideDiscoveryModal(true)}
        />
      )}
    </>
  )
}

const GoalsPage = () => {
  const { loading: loadingAsyncData } = useAsyncData(() => goalsConfig.fetch(), [])
  const { loading: loadingGraphQl, data } = useQuery(GoalsGoalsPageDocument)
  const [showNewGoalModal, setShowNewGoalModal] = useState(false)

  const canSeeClockedIn = data?.attendance._policies.companySeeClockedIn.can ?? false
  const canCreateGoalConfig = data?.apiCore._policies.accessGoalsConfigCreate.can

  const loading = loadingAsyncData || loadingGraphQl

  return (
    <ResponsivePage.Body
      error={null}
      loading={loading}
      actions={{}}
      showHeader
      header={{
        title: i18n.t('surveys.goals.common.goals'),
        description: i18n.t('surveys.goals.index.description'),
        illustration: 'performance_management',
      }}
    >
      <Box gap="s12">
        {canCreateGoalConfig && (
          <Box flexDirection="row" justifyContent="flexEnd">
            <Button iconName="add" onClick={() => setShowNewGoalModal(true)}>
              {i18n.t('surveys.index.actions.new_goal')}
            </Button>
          </Box>
        )}
        <Content canSeeClockedIn={canSeeClockedIn} />
        {showNewGoalModal && <NewGoalModal onClose={() => setShowNewGoalModal(false)} />}
        <Outlet />
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(GoalsPage)
